import React, { useEffect, useState, useCallback } from 'react';
import {
  getCaisseActive, getCaisses, ouvrirCaisse, fermerCaisse,
  addMouvementCaisse, getCaisse,
  getJournalCaissePDF, getRapportCloturePDF, getBonSortiePDF,
} from '../services/api';
import {
  Lock, Unlock, Plus, Minus, CheckCircle, AlertCircle,
  Loader, User, X, History, TrendingUp, Banknote,
  Smartphone, CreditCard, FileCheck, ChevronDown, ChevronUp,
  ShoppingCart, DollarSign, Wallet, Clock, FileText, Download,
  BookOpen, BarChart2, Receipt, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBoutique } from '../context/BoutiqueContext';
import BoutiqueSelector from '../components/BoutiqueSelector';
import './CaisseJournaliere.css';

const fmt     = (n) => (n || 0).toLocaleString('fr-FR') + ' XOF';
const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' }) : '—';

const PAYMENT_ICONS  = { especes: Banknote, wave: Smartphone, orange_money: Smartphone, carte_bancaire: CreditCard, cheque: FileCheck, autre: TrendingUp };
const PAYMENT_LABELS = { especes: 'Espèces', wave: 'Wave', orange_money: 'Orange Money', carte_bancaire: 'Carte', cheque: 'Chèque', autre: 'Autre' };

export default function CaisseJournaliere() {
  const { user } = useAuth();
  const { selectedBoutiqueId } = useBoutique();

  const [loading,     setLoading]     = useState(true);
  const [caisse,      setCaisse]      = useState(null);
  const [historique,  setHistorique]  = useState([]);
  const [showHisto,   setShowHisto]   = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [toast,       setToast]       = useState(null);
  const [submitting,  setSubmitting]  = useState(false);

  const [showOuverture,  setShowOuverture]  = useState(false);
  const [showFermeture,  setShowFermeture]  = useState(false);
  const [showMouvement,  setShowMouvement]  = useState(false);
  const [pdfLoading,     setPdfLoading]     = useState({});

  const [ouvertureData, setOuvertureData] = useState({ fondCaisse: '0', noteOuverture: '' });
  const [fermetureData, setFermetureData] = useState({ montantCompteCaisse: '', fondCaisseLaisse: '0', noteFermeture: '' });
  const [mouvementData, setMouvementData] = useState({ type: 'entree', montant: '', motif: '' });

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const isAdminEntreprise = ['admin-entreprise', 'super-admin'].includes(user?.role);
  const isAdminBoutique   = user?.role === 'admin-boutique';
  const isAdmin           = isAdminEntreprise || isAdminBoutique;

  // boutiqueId à utiliser pour tous les appels API
  const boutiqueIdEffectif = (selectedBoutiqueId && selectedBoutiqueId !== 'all')
    ? selectedBoutiqueId
    : null;

  const loadActive = useCallback(async () => {
    try {
      const { data } = await getCaisseActive(boutiqueIdEffectif || undefined);
      setCaisse(data.data);
    } catch { setCaisse(null); }
  }, [boutiqueIdEffectif]); // eslint-disable-line

  const loadHistorique = useCallback(async () => {
    try {
      const params = { limit: 30 };
      if (boutiqueIdEffectif) params.boutiqueId = boutiqueIdEffectif;
      const { data } = await getCaisses(params);
      setHistorique(data.data || []);
    } catch { setHistorique([]); }
  }, [boutiqueIdEffectif]); // eslint-disable-line

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadActive(), loadHistorique()]);
      setLoading(false);
    };
    init();
  }, [selectedBoutiqueId, loadActive, loadHistorique]);

  useEffect(() => {
    if (!caisse) return;
    const t = setInterval(loadActive, 10000);
    const onFocus = () => loadActive();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [caisse, loadActive]);

  const handleOuvrir = async (e) => {
    e.preventDefault();
    if (isAdmin && (!selectedBoutiqueId || selectedBoutiqueId === 'all')) {
      showToast('error', 'Veuillez sélectionner une boutique avant d\'ouvrir la caisse');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { fondCaisse: parseFloat(ouvertureData.fondCaisse) || 0, noteOuverture: ouvertureData.noteOuverture };
      if (boutiqueIdEffectif) payload.boutiqueId = boutiqueIdEffectif;
      await ouvrirCaisse(payload);
      showToast('success', 'Caisse ouverte avec succès');
      setShowOuverture(false);
      setOuvertureData({ fondCaisse: '0', noteOuverture: '' });
      await Promise.all([loadActive(), loadHistorique()]);
    } catch (err) { showToast('error', err.response?.data?.message || "Erreur lors de l'ouverture"); }
    finally { setSubmitting(false); }
  };

  const handleFermer = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        montantCompteCaisse: parseFloat(fermetureData.montantCompteCaisse) || 0,
        fondCaisseLaisse: parseFloat(fermetureData.fondCaisseLaisse) || 0,
        noteFermeture: fermetureData.noteFermeture
      };
      if (boutiqueIdEffectif) payload.boutiqueId = boutiqueIdEffectif;
      await fermerCaisse(payload);
      showToast('success', 'Caisse fermée avec succès');
      setShowFermeture(false);
      setFermetureData({ montantCompteCaisse: '', fondCaisseLaisse: '0', noteFermeture: '' });
      await Promise.all([loadActive(), loadHistorique()]);
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur lors de la fermeture'); }
    finally { setSubmitting(false); }
  };

  const handleAddMouvement = async (e) => {
    e.preventDefault();
    if (!caisse) return;
    setSubmitting(true);
    try {
      await addMouvementCaisse(caisse._id, { type: mouvementData.type, montant: parseFloat(mouvementData.montant), motif: mouvementData.motif });
      showToast('success', mouvementData.type === 'entree' ? 'Entrée enregistrée' : 'Sortie enregistrée');
      setShowMouvement(false);
      setMouvementData({ type: 'entree', montant: '', motif: '' });
      await loadActive();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const ouvrirPDF = async (key, apiFn, ...args) => {
    setPdfLoading(p => ({ ...p, [key]: true }));
    try {
      const res = await apiFn(...args);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { showToast('error', 'Erreur lors de la génération du document'); }
    finally { setPdfLoading(p => ({ ...p, [key]: false })); }
  };

  const handleViewSession = async (id) => {
    try {
      const { data } = await getCaisse(id);
      setSelectedSession(data.data);
    } catch { showToast('error', 'Impossible de charger la session'); }
  };

  const ecartColor = (v) => {
    if (v === undefined || v === null) return '';
    if (Math.abs(v) < 1) return 'cj-ok';
    return v > 0 ? 'cj-positif' : 'cj-negatif';
  };

  if (loading) return (
    <div className="cj-loading"><Loader className="cj-spinner" size={28}/></div>
  );

  const ecartFermeture = fermetureData.montantCompteCaisse !== ''
    ? parseFloat(fermetureData.montantCompteCaisse) - (caisse?.especesAttendues || 0)
    : null;

  // Fond laissé à la dernière clôture → suggéré à la prochaine ouverture
  const suggestedFond = historique.find(s => s.statut === 'fermee')?.fondCaisseLaisse || 0;
  const ouvrirCaisseForm = () => {
    setOuvertureData({ fondCaisse: String(suggestedFond), noteOuverture: '' });
    setShowOuverture(true);
  };

  return (
    <div className="cj-page">

      {/* Toast */}
      {toast && (
        <div className={`cj-toast cj-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="cj-header">
        <div className="cj-header-left">
          <div className="cj-header-icon">
            {caisse ? <Unlock size={20}/> : <Lock size={20}/>}
          </div>
          <div>
            <h1 className="cj-header-title">Caisse Journalière</h1>
            <p className="cj-header-sub">Gestion des sessions de caisse</p>
          </div>
        </div>
        <div className="cj-header-right">
          {isAdmin && <BoutiqueSelector/>}
          {caisse ? (
            <button className="cj-fermer-btn" onClick={() => { setShowFermeture(v => !v); setShowMouvement(false); }}>
              <Lock size={15}/> Fermer la caisse
            </button>
          ) : (
            <button className="cj-ouvrir-btn" onClick={() => showOuverture ? setShowOuverture(false) : ouvrirCaisseForm()}>
              <Unlock size={15}/> Ouvrir la caisse
            </button>
          )}
        </div>
      </div>

      {/* Alerte boutique non sélectionnée */}
      {isAdmin && !boutiqueIdEffectif && (
        <div className="cj-admin-alert">
          <AlertCircle size={16}/>
          <span>Sélectionnez une boutique dans le menu ci-dessus pour gérer sa caisse.</span>
        </div>
      )}

      <div className="cj-content">

        {/* ══════════ CAISSE OUVERTE ══════════ */}
        {caisse ? (
          <>
            {/* Bande statut */}
            <div className="cj-status-band cj-status-band--open">
              <div className="cj-status-left">
                <span className="cj-dot cj-dot--green"/>
                <span className="cj-status-label">CAISSE OUVERTE</span>
                <span className="cj-status-since">depuis {fmtTime(caisse.dateOuverture)}</span>
              </div>
              <div className="cj-status-right">
                <button className="cj-refresh-btn" onClick={loadActive} title="Actualiser les chiffres">
                  <RefreshCw size={13}/>
                </button>
                <div className="cj-caissier">
                  <User size={13}/>
                  {caisse.caissier?.prenom} {caisse.caissier?.nom}
                </div>
              </div>
            </div>

            {/* KPI */}
            <div className="cj-kpi-row">
              {[
                { label: 'Ventes',           value: caisse.nombreVentes || 0, color:'#6366f1', bg:'#eef2ff', icon: ShoppingCart,  fmt: false },
                { label: 'CA du jour',        value: caisse.totalVentes,       color:'#16a34a', bg:'#dcfce7', icon: TrendingUp,    fmt: true  },
                ...(caisse.totalAcomptes > 0 ? [{ label: 'dont acomptes', value: caisse.totalAcomptes, color:'#0891b2', bg:'#ecfeff', icon: DollarSign, fmt: true }] : []),
                { label: 'Fond de caisse',    value: caisse.fondCaisse,        color:'#3b82f6', bg:'#eff6ff', icon: Wallet,        fmt: true  },
                { label: 'Espèces attendues', value: caisse.especesAttendues,  color:'#f59e0b', bg:'#fffbeb', icon: DollarSign,    fmt: true  },
              ].map((k, i) => {
                const Icon = k.icon;
                return (
                  <div key={i} className="cj-kpi" style={{ borderTopColor: k.color }}>
                    <div className="cj-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                    <div className="cj-kpi-val">{k.fmt ? fmt(k.value) : k.value}</div>
                    <div className="cj-kpi-label">{k.label}</div>
                  </div>
                );
              })}
            </div>

            {/* ── Documents fréquents ── */}
            <div className="cj-docs-row">
              {[
                { key: `journal-${caisse._id}`, icon: BookOpen,  label: 'Journal de caisse',  desc: 'Toutes les opérations',   color:'#6366f1', bg:'#eef2ff', fn: () => ouvrirPDF(`journal-${caisse._id}`, getJournalCaissePDF, caisse._id) },
                { key: `cloture-${caisse._id}`, icon: BarChart2, label: 'Rapport de clôture', desc: 'Bilan & réconciliation',   color:'#f59e0b', bg:'#fffbeb', fn: () => ouvrirPDF(`cloture-${caisse._id}`, getRapportCloturePDF, caisse._id), disabled: caisse.statut === 'ouverte' },
                { key: `sortie-${caisse._id}`,  icon: Receipt,   label: 'Bon de sortie',       desc: 'Pour chaque mouvement sortie', color:'#dc2626', bg:'#fef2f2', fn: null, hint: true },
              ].map(d => {
                const Icon = d.icon;
                const loading = pdfLoading[d.key];
                return (
                  <div key={d.key} className={`cj-doc-card${d.disabled ? ' cj-doc-card--disabled' : ''}`}>
                    <div className="cj-doc-icon" style={{ background: d.bg, color: d.color }}><Icon size={18}/></div>
                    <div className="cj-doc-info">
                      <span className="cj-doc-label">{d.label}</span>
                      <span className="cj-doc-desc">{d.hint ? 'Via liste des mouvements' : d.desc}</span>
                    </div>
                    {!d.hint && (
                      <button
                        className="cj-doc-btn"
                        style={{ borderColor: d.color, color: d.color }}
                        onClick={d.fn}
                        disabled={d.disabled || loading}>
                        {loading ? <Loader size={13} className="cj-spin"/> : <Download size={13}/>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Ventilation paiements — tous les modes affichés, même à 0 */}
            <div className="cj-section">
              <div className="cj-section-title">Ventilation par mode de paiement</div>
              <div className="cj-payment-grid">
                {[
                  { key: 'totalEspeces',       label: 'Espèces',      icon: Banknote,   color: '#16a34a', bg: '#dcfce7' },
                  { key: 'totalWave',          label: 'Wave',         icon: Smartphone, color: '#6366f1', bg: '#eef2ff' },
                  { key: 'totalOrangeMoney',   label: 'Orange Money', icon: Smartphone, color: '#f59e0b', bg: '#fffbeb' },
                  { key: 'totalCarteBancaire', label: 'Carte',        icon: CreditCard, color: '#3b82f6', bg: '#eff6ff' },
                  { key: 'totalCheque',        label: 'Chèque',       icon: FileCheck,  color: '#8b5cf6', bg: '#f5f3ff' },
                  { key: 'totalAutre',         label: 'Autre',        icon: TrendingUp, color: '#64748b', bg: '#f1f5f9' },
                ].map(p => {
                  const Icon = p.icon;
                  const value = caisse[p.key] || 0;
                  const hasValue = value > 0;
                  return (
                    <div key={p.key}
                      className={`cj-payment-item${hasValue ? '' : ' cj-payment-item--empty'}`}
                      style={hasValue ? { borderLeft: `3px solid ${p.color}` } : undefined}>
                      <div className="cj-payment-icon-wrap"
                        style={hasValue ? { background: p.bg, color: p.color } : undefined}>
                        <Icon size={16}/>
                      </div>
                      <div className="cj-payment-amount">{fmt(value)}</div>
                      <div className="cj-payment-label">{p.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mouvements manuels */}
            <div className="cj-section">
              <div className="cj-section-head">
                <div className="cj-section-title">Mouvements manuels
                  {(caisse.totalEntrees || 0) > 0 && <span className="cj-badge cj-badge--green">+{fmt(caisse.totalEntrees)}</span>}
                  {(caisse.totalSorties || 0) > 0 && <span className="cj-badge cj-badge--red">-{fmt(caisse.totalSorties)}</span>}
                </div>
                <button className="cj-add-btn" onClick={() => { setShowMouvement(v => !v); setShowFermeture(false); }}>
                  <Plus size={13}/> Ajouter
                </button>
              </div>

              {showMouvement && (
                <div className="cj-mouvement-form">
                  <div className="cj-type-toggle">
                    <button type="button"
                      className={`cj-type-btn ${mouvementData.type === 'entree' ? 'cj-type-btn--entree' : ''}`}
                      onClick={() => setMouvementData({ ...mouvementData, type: 'entree' })}>
                      <Plus size={13}/> Entrée
                    </button>
                    <button type="button"
                      className={`cj-type-btn ${mouvementData.type === 'sortie' ? 'cj-type-btn--sortie' : ''}`}
                      onClick={() => setMouvementData({ ...mouvementData, type: 'sortie' })}>
                      <Minus size={13}/> Sortie
                    </button>
                  </div>
                  <form onSubmit={handleAddMouvement} className="cj-mouvement-fields">
                    <input
                      type="number" min="1" placeholder="Montant (XOF)"
                      className="cj-input" value={mouvementData.montant} required
                      onChange={e => setMouvementData({ ...mouvementData, montant: e.target.value })}/>
                    <input
                      type="text" placeholder="Motif (ex : dépense transport)"
                      className="cj-input cj-input--wide" value={mouvementData.motif} required
                      onChange={e => setMouvementData({ ...mouvementData, motif: e.target.value })}/>
                    <button type="submit" className="cj-confirm-btn" disabled={submitting}>
                      {submitting ? <Loader size={13} className="cj-spin"/> : <CheckCircle size={13}/>}
                    </button>
                    <button type="button" className="cj-cancel-btn" onClick={() => setShowMouvement(false)}><X size={13}/></button>
                  </form>
                </div>
              )}
            </div>

            {/* Formulaire fermeture */}
            {showFermeture && (
              <div className="cj-fermeture-card">
                <div className="cj-fermeture-head">
                  <div className="cj-fermeture-icon"><Lock size={16}/></div>
                  <span className="cj-fermeture-title">Fermeture de caisse</span>
                </div>

                <div className="cj-recap">
                  {[
                    { label: 'Fond de caisse', value: fmt(caisse.fondCaisse), color: '#374151' },
                    { label: 'Ventes espèces',        value: `+${fmt(caisse.totalEspeces || 0)}`,       color: '#16a34a' },
                    { label: 'Ventes Wave',           value: `+${fmt(caisse.totalWave || 0)}`,          color: '#6366f1', muted: !(caisse.totalWave > 0) },
                    { label: 'Ventes Orange Money',   value: `+${fmt(caisse.totalOrangeMoney || 0)}`,   color: '#f59e0b', muted: !(caisse.totalOrangeMoney > 0) },
                    { label: 'Ventes carte bancaire', value: `+${fmt(caisse.totalCarteBancaire || 0)}`, color: '#3b82f6', muted: !(caisse.totalCarteBancaire > 0) },
                    { label: 'Ventes chèque',         value: `+${fmt(caisse.totalCheque || 0)}`,        color: '#8b5cf6', muted: !(caisse.totalCheque > 0) },
                    { label: 'Ventes autres',         value: `+${fmt(caisse.totalAutre || 0)}`,         color: '#64748b', muted: !(caisse.totalAutre > 0) },
                    ...(caisse.totalEntrees > 0 ? [{ label: 'Entrées manuelles', value: `+${fmt(caisse.totalEntrees)}`, color:'#3b82f6' }] : []),
                    ...(caisse.totalSorties > 0 ? [{ label: 'Sorties manuelles', value: `-${fmt(caisse.totalSorties)}`, color:'#dc2626' }] : []),
                    { label: 'Espèces attendues', value: fmt(caisse.especesAttendues), color: '#0f172a', bold: true, separator: true },
                  ].map((r, i) => (
                    <div key={i}
                      className={`cj-recap-row${r.separator ? ' cj-recap-row--sep' : ''}${r.muted ? ' cj-recap-row--muted' : ''}`}
                      style={{ fontWeight: r.bold ? 700 : 400 }}>
                      <span>{r.label}</span>
                      <span style={{ color: r.color }}>{r.value}</span>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleFermer} className="cj-fermeture-form">
                  <div className="cj-field">
                    <label>Montant compté en caisse (XOF) *</label>
                    <input
                      type="number" min="0" required placeholder="Ex : 203 000"
                      className="cj-input-lg"
                      value={fermetureData.montantCompteCaisse}
                      onChange={e => setFermetureData({ ...fermetureData, montantCompteCaisse: e.target.value })}/>
                    {ecartFermeture !== null && (
                      <div className={`cj-ecart ${ecartColor(ecartFermeture)}`}>
                        Écart : {ecartFermeture >= 0 ? '+' : ''}{fmt(ecartFermeture)}
                      </div>
                    )}
                  </div>
                  <div className="cj-field">
                    <label>Fond de caisse à laisser (XOF) <span className="cj-opt">(optionnel)</span></label>
                    <input
                      type="number" min="0" placeholder="0"
                      className="cj-input-lg"
                      value={fermetureData.fondCaisseLaisse}
                      onChange={e => setFermetureData({ ...fermetureData, fondCaisseLaisse: e.target.value })}/>
                    <span className="cj-hint">
                      Espèces laissées dans le tiroir pour la prochaine ouverture (0 par défaut).
                      {fermetureData.montantCompteCaisse !== '' && (
                        <> Espèces à retirer : <strong>{fmt(Math.max(0, (parseFloat(fermetureData.montantCompteCaisse) || 0) - (parseFloat(fermetureData.fondCaisseLaisse) || 0)))}</strong></>
                      )}
                    </span>
                  </div>
                  <div className="cj-field">
                    <label>Note de fermeture <span className="cj-opt">(optionnel)</span></label>
                    <textarea rows="2" placeholder="Remarques…"
                      value={fermetureData.noteFermeture}
                      onChange={e => setFermetureData({ ...fermetureData, noteFermeture: e.target.value })}/>
                  </div>
                  <div className="cj-fermeture-actions">
                    <button type="button" className="cj-btn-cancel" onClick={() => setShowFermeture(false)} disabled={submitting}>Annuler</button>
                    <button type="submit" className="cj-fermer-btn" disabled={submitting}>
                      {submitting ? <><Loader size={14} className="cj-spin"/> Fermeture…</> : <><Lock size={14}/> Confirmer la fermeture</>}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        ) : (
          /* ══════════ CAISSE FERMÉE ══════════ */
          <>
            <div className="cj-status-band cj-status-band--closed">
              <div className="cj-status-left">
                <span className="cj-dot cj-dot--red"/>
                <span className="cj-status-label">CAISSE FERMÉE</span>
                <span className="cj-status-since">Aucune session active</span>
              </div>
            </div>

            {!showOuverture ? (
              <div className="cj-closed-state">
                <div className="cj-closed-icon"><Lock size={36}/></div>
                <p className="cj-closed-text">Aucune session ouverte pour cette boutique</p>
                <button className="cj-ouvrir-btn" onClick={ouvrirCaisseForm}>
                  <Unlock size={15}/> Ouvrir la caisse
                </button>
              </div>
            ) : (
              <div className="cj-ouverture-card">
                <div className="cj-fermeture-head">
                  <div className="cj-ouvrir-icon"><Unlock size={16}/></div>
                  <span className="cj-fermeture-title">Ouverture de caisse</span>
                </div>
                <form onSubmit={handleOuvrir} className="cj-fermeture-form">
                  <div className="cj-field">
                    <label>Fond de caisse (XOF) <span className="cj-opt">(optionnel)</span></label>
                    <input
                      type="number" min="0" autoFocus placeholder="0"
                      className="cj-input-lg"
                      value={ouvertureData.fondCaisse}
                      onChange={e => setOuvertureData({ ...ouvertureData, fondCaisse: e.target.value })}/>
                    <span className="cj-hint">Montant en espèces présent en caisse à l'ouverture (0 par défaut)</span>
                  </div>
                  <div className="cj-field">
                    <label>Note d'ouverture <span className="cj-opt">(optionnel)</span></label>
                    <textarea rows="2" placeholder="Remarques…"
                      value={ouvertureData.noteOuverture}
                      onChange={e => setOuvertureData({ ...ouvertureData, noteOuverture: e.target.value })}/>
                  </div>
                  <div className="cj-fermeture-actions">
                    <button type="button" className="cj-btn-cancel" onClick={() => setShowOuverture(false)} disabled={submitting}>Annuler</button>
                    <button type="submit" className="cj-ouvrir-btn" disabled={submitting}>
                      {submitting ? <><Loader size={14} className="cj-spin"/> Ouverture…</> : <><Unlock size={14}/> Ouvrir</>}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}

        {/* ── Historique ── */}
        <div className="cj-histo-section">
          <button className="cj-histo-toggle" onClick={() => setShowHisto(v => !v)}>
            <History size={15}/>
            <span>Historique des sessions ({historique.length})</span>
            {showHisto ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
          </button>

          {showHisto && (
            <div className="cj-card">
              <table className="cj-table">
                <thead>
                  <tr>
                    <th>Ouverture</th>
                    <th>Fermeture</th>
                    <th>Caissier</th>
                    <th>Fond</th>
                    <th>Ventes</th>
                    <th>CA</th>
                    <th>Écart</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {historique.length === 0 ? (
                    <tr><td colSpan="9" className="cj-empty-row">Aucune session enregistrée</td></tr>
                  ) : historique.map(s => (
                    <tr key={s._id} className={s.statut === 'ouverte' ? 'cj-row--active' : ''}>
                      <td className="cj-date">{fmtDate(s.dateOuverture)}</td>
                      <td className="cj-date">{s.dateFermeture ? fmtDate(s.dateFermeture) : <span className="cj-muted">—</span>}</td>
                      <td className="cj-caissier-cell">
                        <User size={11}/> {s.caissier?.prenom} {s.caissier?.nom}
                      </td>
                      <td className="cj-amount">{fmt(s.fondCaisse)}</td>
                      <td className="cj-num">{s.nombreVentes || 0}</td>
                      <td className="cj-amount cj-amount--green">{fmt(s.totalVentes)}</td>
                      <td>
                        {s.ecartEspeces !== undefined && s.ecartEspeces !== null ? (
                          <span className={`cj-ecart-badge ${ecartColor(s.ecartEspeces)}`}>
                            {s.ecartEspeces >= 0 ? '+' : ''}{fmt(s.ecartEspeces)}
                          </span>
                        ) : <span className="cj-muted">—</span>}
                      </td>
                      <td>
                        <span className={`cj-statut-badge ${s.statut === 'ouverte' ? 'cj-statut--open' : 'cj-statut--closed'}`}>
                          {s.statut === 'ouverte' ? 'Ouverte' : 'Fermée'}
                        </span>
                      </td>
                      <td>
                        <div className="cj-histo-actions">
                          <button className="cj-detail-btn" onClick={() => handleViewSession(s._id)}>Détail</button>
                          <button className="cj-pdf-btn" title="Journal de caisse"
                            disabled={!!pdfLoading[`j-${s._id}`]}
                            onClick={() => ouvrirPDF(`j-${s._id}`, getJournalCaissePDF, s._id)}>
                            {pdfLoading[`j-${s._id}`] ? <Loader size={11} className="cj-spin"/> : <BookOpen size={11}/>}
                          </button>
                          {s.statut === 'fermee' && (
                            <button className="cj-pdf-btn cj-pdf-btn--gold" title="Rapport de clôture"
                              disabled={!!pdfLoading[`c-${s._id}`]}
                              onClick={() => ouvrirPDF(`c-${s._id}`, getRapportCloturePDF, s._id)}>
                              {pdfLoading[`c-${s._id}`] ? <Loader size={11} className="cj-spin"/> : <BarChart2 size={11}/>}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal détail session ── */}
      {selectedSession && (
        <>
          <div className="cj-overlay" onClick={() => setSelectedSession(null)}/>
          <div className="cj-modal">
            <div className="cj-modal-head">
              <div>
                <h2 className="cj-modal-title"><Clock size={15}/> Détail de session</h2>
                <p className="cj-modal-sub">
                  {fmtDate(selectedSession.dateOuverture)}
                  {selectedSession.dateFermeture && ` → ${fmtDate(selectedSession.dateFermeture)}`}
                </p>
              </div>
              <button className="cj-modal-close" onClick={() => setSelectedSession(null)}><X size={16}/></button>
            </div>
            <div className="cj-modal-body">

              {/* Résumé */}
              <div className="cj-detail-grid">
                {[
                  { label: 'Caissier',        value: `${selectedSession.caissier?.prenom || ''} ${selectedSession.caissier?.nom || ''}` },
                  { label: 'Fond de caisse',   value: fmt(selectedSession.fondCaisse) },
                  { label: 'Nombre de ventes', value: selectedSession.nombreVentes || 0 },
                  { label: 'CA Total',         value: fmt(selectedSession.totalVentes), large: true },
                ].map((d, i) => (
                  <div key={i} className="cj-detail-item">
                    <span className="cj-detail-label">{d.label}</span>
                    <span className={`cj-detail-value ${d.large ? 'cj-detail-value--lg' : ''}`}>{d.value}</span>
                  </div>
                ))}
              </div>

              {/* Ventilation */}
              {Object.entries({
                especes: selectedSession.totalEspeces, wave: selectedSession.totalWave,
                orange_money: selectedSession.totalOrangeMoney, carte_bancaire: selectedSession.totalCarteBancaire,
                cheque: selectedSession.totalCheque, autre: selectedSession.totalAutre,
              }).some(([, v]) => (v || 0) > 0) && (
                <>
                  <div className="cj-detail-section-title">Ventilation des paiements</div>
                  <div className="cj-detail-payments">
                    {Object.entries({
                      especes: selectedSession.totalEspeces, wave: selectedSession.totalWave,
                      orange_money: selectedSession.totalOrangeMoney, carte_bancaire: selectedSession.totalCarteBancaire,
                      cheque: selectedSession.totalCheque, autre: selectedSession.totalAutre,
                    }).filter(([, v]) => (v || 0) > 0).map(([k, v]) => {
                      const Icon = PAYMENT_ICONS[k] || TrendingUp;
                      return (
                        <div key={k} className="cj-detail-payment-row">
                          <Icon size={14}/>
                          <span>{PAYMENT_LABELS[k]}</span>
                          <span className="cj-detail-payment-amount">{fmt(v)}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Réconciliation */}
              {selectedSession.statut === 'fermee' && (
                <>
                  <div className="cj-detail-section-title">Réconciliation espèces</div>
                  <div className="cj-recon">
                    <div className="cj-recon-row"><span>Espèces attendues</span><span>{fmt(selectedSession.especesAttendues)}</span></div>
                    <div className="cj-recon-row"><span>Montant compté</span><span>{fmt(selectedSession.montantCompteCaisse)}</span></div>
                    <div className={`cj-recon-row cj-recon-ecart ${ecartColor(selectedSession.ecartEspeces)}`}>
                      <strong>Écart</strong>
                      <strong>{selectedSession.ecartEspeces >= 0 ? '+' : ''}{fmt(selectedSession.ecartEspeces)}</strong>
                    </div>
                    <div className="cj-recon-row"><span>Fond laissé</span><span>{fmt(selectedSession.fondCaisseLaisse || 0)}</span></div>
                    <div className="cj-recon-row"><span>Espèces retirées</span><span>{fmt(selectedSession.especesRetirees ?? Math.max(0, (selectedSession.montantCompteCaisse || 0) - (selectedSession.fondCaisseLaisse || 0)))}</span></div>
                  </div>
                  {selectedSession.noteFermeture && (
                    <p className="cj-note"><strong>Note :</strong> {selectedSession.noteFermeture}</p>
                  )}
                </>
              )}

              {/* Mouvements */}
              {selectedSession.mouvements?.length > 0 && (
                <>
                  <div className="cj-detail-section-title">Mouvements de caisse</div>
                  <div className="cj-detail-mouvements">
                    {selectedSession.mouvements.map(m => (
                      <div key={m._id} className={`cj-mouvement-row cj-mouvement-row--${m.type}`}>
                        <span className="cj-mouvement-icon">
                          {m.type === 'entree' ? <Plus size={12}/> : <Minus size={12}/>}
                        </span>
                        <span className="cj-mouvement-motif">{m.motif}</span>
                        <span className="cj-mouvement-amount">{m.type === 'sortie' ? '-' : '+'}{fmt(m.montant)}</span>
                        <span className="cj-mouvement-time">{fmtTime(m.createdAt)}</span>
                        <button
                          className="cj-bon-btn"
                          title="Bon de sortie/entrée"
                          disabled={!!pdfLoading[`bon-${m._id}`]}
                          onClick={() => ouvrirPDF(`bon-${m._id}`, getBonSortiePDF, selectedSession._id, m._id)}>
                          {pdfLoading[`bon-${m._id}`] ? <Loader size={11} className="cj-spin"/> : <FileText size={11}/>}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
